import * as React from "react";
import { I18n } from "react-redux-i18n";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@common/components/modal';;
import isNil from 'lodash/isNil';

import { getTestAttributes } from "@common/utils/tests";
import { Flex } from "@common/components/Flex";
import { HttpErrorDto } from "@common/dto/http";
import { Button } from "@common/components/Button";
import { FolderListDto } from "@common/dto/folder/list-dto";
import Tree, { isParentOfChild } from "@common/components/Tree";

import { MoveFolderDto } from './move-folder.types';
import { TreeGetIdArgs } from "../Tree/tree.types";

import './move-folder.styles.scss';

type MoveFolderProps = {
  onClose: () => void;
  onSubmit: (movedFolderDto: MoveFolderDto) => void;
  folder: FolderListDto;
  errors?: string[];
  folders: FolderListDto[];
  showErrorMessage: (message?: string) => void;
  rootFolderId?: number | string;
}

export const DEFAULT_ROOT_FOLDER_ID = 0;

export const MoveFolder = ({ onClose, onSubmit, folder, folders, showErrorMessage, rootFolderId = DEFAULT_ROOT_FOLDER_ID }: MoveFolderProps) => {

  const [loading, setLoading] = React.useState(false);
  const [selectedFolder, setSelectedFolder] = React.useState<FolderListDto>();

  const disableMoveBtn = loading || isNil(selectedFolder?.id);

  const handleSelectedFolderChange = React.useCallback((id: FolderListDto['id'], folder: FolderListDto) => {
    setSelectedFolder(folder);
  }, [setSelectedFolder]);

  const shouldDisableItem = React.useCallback((item: FolderListDto) => {

    const checks = [
      () => {
        return folder.id == item.id;
      },
      () => {
        return isParentOfChild(folder, item);
      }
    ];

    return checks.some((compute) => compute());

  }, [folder.id, selectedFolder?.id]);

  const handleSubmit = async () => {
    
    if(folder.parentid == selectedFolder!.id) {
      return showErrorMessage(I18n.t('The folder is already there'));
    }

    try {
      setLoading(true);
      await onSubmit({
        folderId: folder.id,
        newFolderId: selectedFolder!.id
      });
    } catch (err) {
      console.error(err);
      setLoading(false);
      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to move folder');
      showErrorMessage(errorMessage);
    }
  };

  const modifiedFolders = React.useMemo(() => [
    {
      id: rootFolderId, 
      name: I18n.t("Pedigree Folders"),
      imageURL: 'gif/pedigree.gif',
      imageURLSelected: 'gif/pedigree.gif',
      children: folders
    }
  ], [folders]);

  return (
    <Modal
      show
      bsSize="medium"
      bsClass="MoveFolder bootstrap modal"
      onHide={onClose}
      onEscapeKeyDown={onClose}
      animation={false}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle
          {...getTestAttributes({ id: 'lbl-move-folder-wnd-header' })}
        >
          {I18n.t("Move Folder")}
        </ModalTitle>
      </ModalHeader>
      <ModalBody
        className="MoveFolder__body" 
        {...getTestAttributes({ id: 'fp-move-folder-wnd-content-panel' })}
      >
        <p {...getTestAttributes({ id: 'lbl-move-folder-wnd-description' })}>{I18n.t('Select where to move the folder to')}</p>
        <div className="MoveFolder__folders">
          <Tree
            getId={{
              folder: ({ parentIndex, ownIndex, data }: TreeGetIdArgs) => parentIndex === undefined 
                ? `folder-${ownIndex}` 
                : `folder-child-${data.id}`,
              collapseIcon: ({ parentIndex, ownIndex, data }: TreeGetIdArgs) => parentIndex === undefined 
                ? `folder-${ownIndex}-exp-icon` 
                : `folder-child-${data.id}-exp-icon`,
            }}
            collapseAfterLevel={1}
            items={modifiedFolders}
            value={selectedFolder?.id}
            onChange={handleSelectedFolderChange}
            shouldDisableItem={shouldDisableItem}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Flex justify="flex-end">
          <Button
            onClick={onClose}
            disabled={loading}
            {...getTestAttributes({ id: 'btn-move-folder-wnd-cancel' })}
          >
            {I18n.t('Cancel')}
          </Button>
          <Button
            bsStyle="primary"
            onClick={handleSubmit}
            disabled={disableMoveBtn}
            {...getTestAttributes({ id: 'btn-move-folder-wnd-move' })}
          >
            {I18n.t("Move")}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  )
}